import { jsx as _jsx } from "react/jsx-runtime";
import { useLDClient } from "launchdarkly-react-client-sdk";
import debounce from "lodash/debounce";
import noop from "lodash/noop";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { convertUnknownToError, reportErrorToBugsnag } from "~/utils/ErrorUtils";
const defaultValue = {
    identify: noop
};
const LaunchDarklyProviderContext = /*#__PURE__*/ createContext(defaultValue);
export const LaunchDarklyProvider = (param)=>{
    let { children } = param;
    const ldClient = useLDClient();
    const userRef = useRef();
    const debouncedIdentify = useMemo(()=>debounce(async ()=>{
            if (userRef.current) {
                try {
                    await (ldClient === null || ldClient === void 0 ? void 0 : ldClient.identify(userRef.current));
                } catch (error) {
                    const typedError = convertUnknownToError(error);
                    // ignore network error to prevent unhandled promise being thrown and logging that to bugsnag
                    if (typedError.message !== "network error (Error)") {
                        throw error;
                    }
                }
            }
        }, 250), [
        ldClient
    ]);
    const identify = useCallback((user)=>{
        userRef.current = {
            kind: "user",
            ...userRef.current,
            ...user
        };
        debouncedIdentify();
    }, [
        debouncedIdentify
    ]);
    const value = useMemo(()=>({
            identify
        }), [
        identify
    ]);
    return /*#__PURE__*/ _jsx(LaunchDarklyProviderContext.Provider, {
        value: value,
        children: children
    });
};
export const useLaunchDarklyContext = ()=>{
    const context = useContext(LaunchDarklyProviderContext);
    if (context === defaultValue) {
        const error = "LaunchDarklyProviderContext used outside of LaunchDarklyProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
